/**
 * ProfileCompletionMeter — card that surfaces a 0–100% completion score
 * built from useProfileCompletion. Renders a progress ring + the next
 * 3 actionable items so the member always has an obvious next step.
 *
 * At 100% we celebrate and surface the "free Boost" reward CTA — the
 * actual entitlement grant is handled separately (a la carte / engagement
 * server logic), but the UI promises and surfaces it here.
 */
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Sparkles } from "lucide-react";
import { useProfileCompletion } from "@/lib/profile/useProfileCompletion";

const ProfileCompletionMeter = () => {
  const { loading, percent, todo, isComplete } = useProfileCompletion();

  if (loading) return null;

  const upNext = todo.slice(0, 3);

  return (
    <section
      aria-labelledby="profile-completion-heading"
      className="mt-8 rounded-2xl border border-border bg-card p-6"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2
            id="profile-completion-heading"
            className="text-lg font-semibold flex items-center gap-2"
          >
            Profile completion
            {isComplete && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 text-amber-600 px-2 py-0.5 text-xs font-medium">
                <Sparkles className="h-3 w-3" aria-hidden /> 100%
              </span>
            )}
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {isComplete
              ? "You're all set. Claim your free Boost reward below."
              : "Complete your profile for better matches and a free Boost at 100%."}
          </p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold tabular-nums">{percent}%</p>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
            {todo.length === 0
              ? "Complete"
              : `${todo.length} item${todo.length === 1 ? "" : "s"} left`}
          </p>
        </div>
      </div>

      {/* progress bar */}
      <div
        className="mt-4 h-2 w-full rounded-full bg-secondary overflow-hidden"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-label="Profile completion percent"
      >
        <div
          className={`h-full transition-all duration-500 ${
            isComplete
              ? "bg-gradient-to-r from-amber-400 to-amber-500"
              : "bg-foreground"
          }`}
          style={{ width: `${percent}%` }}
        />
      </div>

      {/* up-next list */}
      {upNext.length > 0 && (
        <ul className="mt-5 space-y-2">
          {upNext.map((item) => (
            <li key={item.key}>
              <Link
                to={item.href}
                className="flex items-start gap-3 rounded-xl border border-border/60 px-3 py-2.5 text-sm hover:bg-secondary/40 transition-colors"
              >
                <Circle
                  className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0"
                  aria-hidden
                />
                <span className="flex-1">
                  <span className="font-medium">{item.label}</span>
                  {item.hint && (
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      {item.hint}
                    </span>
                  )}
                </span>
                <span className="text-xs text-muted-foreground shrink-0">
                  +{item.weight}%
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {isComplete && (
        <div className="mt-5 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" aria-hidden />
          <div className="flex-1 text-sm">
            <p className="font-medium">Free Boost unlocked</p>
            <p className="text-xs text-muted-foreground mt-1">
              Claim a complimentary 30-minute Boost as a thank-you for completing
              your profile.
            </p>
          </div>
          <Link
            to="/a-la-carte?reward=completion_boost"
            className="rounded-full bg-foreground text-background px-3 py-1.5 text-xs font-medium hover:opacity-90 transition-opacity shrink-0"
          >
            Claim
          </Link>
        </div>
      )}
    </section>
  );
};

export default ProfileCompletionMeter;
